import { useCallback, useEffect, useRef, useState } from "react";
import SVG from "../SVG";

export default function FilterOverflowRow({ children }) {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateScrollHints = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const items = Array.from(container.children);
    if (items.length === 0 || container.scrollWidth <= container.clientWidth) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }

    const containerBounds = container.getBoundingClientRect();
    const itemBounds = items.map((item) => item.getBoundingClientRect());
    const contentLeft = Math.min(...itemBounds.map((bounds) => bounds.left));
    const contentRight = Math.max(...itemBounds.map((bounds) => bounds.right));
    const edgeTolerance = 1;

    setShowLeftArrow(contentLeft < containerBounds.left - edgeTolerance);
    setShowRightArrow(contentRight > containerBounds.right + edgeTolerance);
  }, []);

  const scrollFilters = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distance = Math.max(container.clientWidth * 0.75, 120);
    container.scrollBy({
      left: direction === "left" ? -distance : distance,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const frameId = requestAnimationFrame(updateScrollHints);
    const resizeObserver = new ResizeObserver(updateScrollHints);
    resizeObserver.observe(container);
    Array.from(container.children).forEach((item) =>
      resizeObserver.observe(item),
    );
    window.addEventListener("resize", updateScrollHints);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollHints);
    };
  }, [updateScrollHints]);

  return (
    <div className="flex w-full flex-col gap-2.5 pt-px">
      <div className="flex w-full items-center justify-start gap-2.5 overflow-hidden">
        <span className="shrink-0 text-xs font-light text-zi-text-light dark:text-zi-text-dark">
          فیلتر:
        </span>

        <div className="relative min-w-0 flex-1">
          <div
            ref={scrollContainerRef}
            onScroll={updateScrollHints}
            className="flex min-w-0 flex-1 items-center justify-start gap-2.5 overflow-x-auto overflow-y-visible"
          >
            {children}
          </div>

          {showLeftArrow && (
            <button
              type="button"
              aria-label="نمایش فیلترهای بعدی"
              onClick={() => scrollFilters("left")}
              className="absolute -left-3 top-1/2 z-30 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/40 opacity-60 shadow-md backdrop-blur-md transition-opacity hocus:opacity-100 dark:bg-black/30"
            >
              <SVG
                name="arrowLeft"
                classname="size-3 stroke-zi-blue-dark dark:stroke-white"
              />
            </button>
          )}

          {showRightArrow && (
            <button
              type="button"
              aria-label="نمایش فیلترهای قبلی"
              onClick={() => scrollFilters("right")}
              className="absolute -right-3 top-1/2 z-30 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/40 opacity-60 shadow-md backdrop-blur-md transition-opacity hocus:opacity-100 dark:bg-black/30"
            >
              <SVG
                name="arrowLeft"
                classname="size-3 rotate-180 stroke-zi-blue-dark dark:stroke-white"
              />
            </button>
          )}
        </div>
      </div>

      <div className="card-divider h-px w-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)]" />
    </div>
  );
}
